import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Loader2, Copy, Check, Code, Wifi, Zap, Mail, AlertCircle, X, ChevronDown, ChevronRight,
} from "lucide-react";
import WebhookHealthPanel from "./WebhookHealthPanel";
import { toast } from "sonner";
import api from "../../lib/api";

export function WebhookInfoCard({ restaurantId }) {
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState({});
  const [showExample, setShowExample] = useState(false);

  const [urlChanged, setUrlChanged] = useState(false);
  const [urlChangeDismissed, setUrlChangeDismissed] = useState(false);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyName, setNotifyName] = useState("");
  const [sendingNotify, setSendingNotify] = useState(false);

  const prevUrlRef = useRef(null);

  const loadWebhook = useCallback(async () => {
    try {
      const data = await api.request(`/restaurants/${restaurantId}/webhooks`);
      setWebhookConfig(data);
      if (data?.webhook_url) {
        if (prevUrlRef.current && prevUrlRef.current !== data.webhook_url) setUrlChanged(true);
        prevUrlRef.current = data.webhook_url;
      }
    } catch {}
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { if (restaurantId) loadWebhook(); }, [restaurantId, loadWebhook]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const data = await api.request(`/restaurants/${restaurantId}/webhooks/generate`, { method: "POST" });
      setWebhookConfig(data);
      if (data?.webhook_url) {
        if (prevUrlRef.current && prevUrlRef.current !== data.webhook_url) setUrlChanged(true);
        prevUrlRef.current = data.webhook_url;
      }
      toast.success("Webhook generado correctamente");
    } catch { toast.error("Error al generar webhook"); }
    finally { setGenerating(false); }
  };

  const copyText = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(prev => ({ ...prev, [key]: true }));
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    toast.success("Copiado");
  };

  const handleSendNotification = async () => {
    if (!notifyEmail.trim()) { toast.error("Introduce un email"); return; }
    setSendingNotify(true);
    try {
      await api.request(`/restaurants/${restaurantId}/webhooks/notify-url-change`, {
        method: "POST",
        body: JSON.stringify({ email: notifyEmail.trim(), recipient_name: notifyName.trim() || "Equipo TPV" }),
      });
      toast.success(`Email enviado a ${notifyEmail}`);
      setShowNotifyDialog(false);
      setUrlChangeDismissed(true);
    } catch {
      toast.error("Error al enviar el email. Verifica la configuración de Resend.");
    } finally { setSendingNotify(false); }
  };

  if (loading) return null;

  const examplePayload = JSON.stringify({
    product_name: "Paella Valenciana", quantity: 2, unit_price: 14.50, total: 29.00,
    payment_method: "card", date: new Date().toISOString(), ticket_number: "77489",
    table: "12", seller: "1", base_tax: 26.36, tax_amount: 2.64, ticket_total: 29.00,
  }, null, 2);

  const curlExample = webhookConfig?.webhook_url
    ? `curl -X POST "${webhookConfig.webhook_url}" \\\n  -H "Content-Type: application/json" \\\n  -H "X-Webhook-Secret: ${webhookConfig.webhook_secret || "<tu-secret>"}" \\\n  -d '{"product_name":"Paella","quantity":2,"unit_price":14.50,"total":29.00,"payment_method":"card","date":"${new Date().toISOString()}","ticket_number":"77489","table":"12","seller":"1","base_tax":26.36,"tax_amount":2.64,"ticket_total":29.00}'`
    : "";

  return (
    <>
      {urlChanged && !urlChangeDismissed && webhookConfig?.webhook_url && (
        <div
          data-testid="webhook-url-changed-banner"
          className="relative flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700"
        >
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mt-0.5">
            <AlertCircle className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">La URL del webhook ha cambiado</p>
            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 leading-relaxed">
              Tu TPV podría estar enviando ventas a la URL antigua. Notifica a tu equipo técnico para que actualice la configuración con la nueva URL.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                className="h-7 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white border-0"
                onClick={() => setShowNotifyDialog(true)}
                data-testid="btn-notify-url-change"
              >
                <Mail className="w-3 h-3" />
                Enviar nueva URL por email
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                onClick={() => setUrlChangeDismissed(true)}
                data-testid="btn-dismiss-url-change"
              >
                Descartar
              </Button>
            </div>
          </div>
          <button
            className="absolute top-3 right-3 text-amber-500 hover:text-amber-700 transition-colors"
            onClick={() => setUrlChangeDismissed(true)}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              Notificar cambio de URL al equipo TPV
            </DialogTitle>
            <DialogDescription>
              Se enviará un email con la nueva URL, el secret y un ejemplo cURL para que tu equipo actualice la configuración del TPV.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="notify-email" className="text-sm">Email del técnico / equipo TPV *</Label>
              <Input
                id="notify-email"
                type="email"
                placeholder="tecnico@miempresa.com"
                value={notifyEmail}
                onChange={e => setNotifyEmail(e.target.value)}
                className="mt-1.5"
                data-testid="input-notify-email"
              />
            </div>
            <div>
              <Label htmlFor="notify-name" className="text-sm">Nombre del destinatario (opcional)</Label>
              <Input
                id="notify-name"
                placeholder="Ej: Juan García"
                value={notifyName}
                onChange={e => setNotifyName(e.target.value)}
                className="mt-1.5"
                data-testid="input-notify-name"
              />
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">El email incluirá:</p>
              <ul className="space-y-0.5 ml-2">
                <li>• La nueva URL del webhook</li>
                <li>• El Secret (header X-Webhook-Secret)</li>
                <li>• Ejemplo cURL de verificación</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNotifyDialog(false)} disabled={sendingNotify}>Cancelar</Button>
            <Button onClick={handleSendNotification} disabled={sendingNotify || !notifyEmail.trim()} data-testid="btn-send-notify-email">
              {sendingNotify
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Enviando...</>
                : <><Mail className="w-4 h-4 mr-2" />Enviar email</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/10" data-testid="webhook-info-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-green-700 dark:text-green-400">
            <Zap className="w-4 h-4" />
            Configuración de Webhook
          </CardTitle>
          <CardDescription>URL a la que tu TPV debe enviar los datos de ventas en tiempo real</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {webhookConfig?.webhook_url ? (
            <>
              <div>
                <Label className="text-xs text-muted-foreground mb-1 block">URL del Webhook</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2.5 bg-background rounded-lg border text-xs font-mono break-all">
                    {webhookConfig.webhook_url}
                  </code>
                  <Button size="icon" variant="outline" onClick={() => copyText(webhookConfig.webhook_url, "url")} className="flex-shrink-0" data-testid="btn-copy-webhook-url">
                    {copied.url ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {webhookConfig.webhook_secret && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">
                    Secret <span className="ml-1 text-[10px] bg-muted px-1.5 py-0.5 rounded">Header: X-Webhook-Secret</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2.5 bg-background rounded-lg border text-xs font-mono break-all">
                      {webhookConfig.webhook_secret}
                    </code>
                    <Button size="icon" variant="outline" onClick={() => copyText(webhookConfig.webhook_secret, "secret")} className="flex-shrink-0">
                      {copied.secret ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {webhookConfig.webhook_url && (
                <details className="rounded-lg border bg-background/70 text-xs" data-testid="vendor-webhooks-section">
                  <summary className="cursor-pointer list-none flex items-center gap-1.5 px-3 py-2 font-medium text-foreground select-none">
                    <ChevronRight className="w-3.5 h-3.5 shrink-0 transition-transform [details[open]>summary_&]:rotate-90" />
                    URLs para TPVs populares (Revo · TpvPC · Glop · Ágora · Cuiner · Ordersline)
                  </summary>
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    <p className="text-muted-foreground text-[11px] leading-relaxed">
                      Si tu TPV es uno de los soportados, úsa la URL específica:
                      aceptamos el payload nativo del vendor sin que tengas que
                      mapear campos. El mismo <code>Secret</code> arriba funciona
                      para todas.
                    </p>
                    {["revo", "tpvpc", "glop", "agora", "cuiner", "ordersline"].map((v) => {
                      const vendorUrl = webhookConfig.webhook_url.replace(
                        /\/api\/webhooks\/([^/]+)$/, `/api/webhooks/${v}/$1`,
                      );
                      return (
                        <div key={v} className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] uppercase shrink-0 w-14 justify-center">{v}</Badge>
                          <code className="flex-1 p-1.5 bg-muted/50 rounded border text-[11px] font-mono break-all">
                            {vendorUrl}
                          </code>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 shrink-0"
                            onClick={() => copyText(vendorUrl, `url-${v}`)}
                            data-testid={`btn-copy-vendor-url-${v}`}
                          >
                            {copied[`url-${v}`] ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Wifi className="w-3.5 h-3.5 text-green-500" />
                  {webhookConfig.events_received || 0} eventos recibidos
                </span>
                <Badge variant={webhookConfig.enabled ? "default" : "secondary"} className="text-xs">
                  {webhookConfig.enabled ? "Activo" : "Inactivo"}
                </Badge>
              </div>

              <WebhookHealthPanel restaurantId={restaurantId} />

              <div className="border rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-muted/50 transition-colors text-left"
                  onClick={() => setShowExample(v => !v)}
                  data-testid="btn-toggle-webhook-example"
                >
                  <span className="flex items-center gap-2">
                    <Code className="w-4 h-4 text-primary" />
                    Cómo configurar tu TPV para usar este webhook
                  </span>
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showExample ? "rotate-180" : ""}`} />
                </button>

                {showExample && (
                  <div className="border-t bg-muted/20 p-4 space-y-5">
                    <div className="space-y-3">
                      {[
                        { n: 1, title: "Copia la URL y el Secret", desc: "Usa los botones de copia de arriba para obtener la URL y el Secret." },
                        { n: 2, title: "Accede a la configuración de tu TPV", desc: "En tu software TPV, busca la sección de integraciones, webhooks o notificaciones HTTP." },
                        { n: 3, title: "Añade el webhook", desc: <>Pega la URL y configura el header <code className="bg-muted px-1 py-0.5 rounded text-xs">X-Webhook-Secret</code> con el valor del Secret.</> },
                        { n: 4, title: "Activa el disparo en cada venta", desc: "Configura tu TPV para que envíe una petición POST a esta URL tras cada ticket o cierre de mesa." },
                        { n: 5, title: "Prueba con el botón 'Evento prueba'", desc: "Ve a la pestaña En Vivo y pulsa 'Evento prueba' para verificar que el canal funciona." },
                      ].map(({ n, title, desc }) => (
                        <div key={n} className="flex gap-3">
                          <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{n}</span>
                          <div>
                            <p className="text-sm font-medium">{title}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Formato del JSON (body)</p>
                        <Button size="sm" variant="ghost" className="h-6 text-xs gap-1 px-2" onClick={() => copyText(examplePayload, "payload")}>
                          {copied.payload ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          Copiar
                        </Button>
                      </div>
                      <pre className="bg-zinc-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto leading-relaxed font-mono">{examplePayload}</pre>
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                        {[
                          ["product_name", "Nombre del artículo vendido"],
                          ["quantity", "Unidades vendidas"],
                          ["unit_price", "Precio unitario (€)"],
                          ["total", "Importe total del ticket"],
                          ["payment_method", "card · cash · bizum"],
                          ["date", "Fecha ISO 8601 (opcional)"],
                        ].map(([field, desc]) => (
                          <div key={field} className="flex gap-1.5">
                            <code className="text-primary shrink-0">{field}</code>
                            <span>— {desc}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {curlExample && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ejemplo con cURL</p>
                          <Button size="sm" variant="ghost" className="h-6 text-xs gap-1 px-2" onClick={() => copyText(curlExample, "curl")}>
                            {copied.curl ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            Copiar
                          </Button>
                        </div>
                        <pre className="bg-zinc-900 text-blue-300 text-xs p-3 rounded-lg overflow-x-auto leading-relaxed font-mono whitespace-pre-wrap break-all">{curlExample}</pre>
                      </div>
                    )}

                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Dónde encontrar la opción en TPVs comunes</p>
                      <div className="grid sm:grid-cols-2 gap-2">
                        {[
                          { name: "Square POS", path: "Dashboard → Integraciones → Webhooks" },
                          { name: "Lightspeed", path: "Configuración → API & Webhooks → Añadir endpoint" },
                          { name: "Revo XEF", path: "Backoffice → Integraciones → Notificaciones HTTP" },
                          { name: "Toast POS", path: "Technology → Partner Connect → Webhooks" },
                          { name: "Tiller / SumUp", path: "Configuración → Integraciones → Webhook" },
                          { name: "Otros TPVs", path: "Busca 'webhooks', 'HTTP callback' o 'notificaciones POST'" },
                        ].map(({ name, path }) => (
                          <div key={name} className="flex items-start gap-2 p-2 rounded-lg bg-background border text-xs">
                            <ChevronRight className="w-3.5 h-3.5 text-primary mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium">{name}</p>
                              <p className="text-muted-foreground">{path}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <Wifi className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-3">Genera tu URL de webhook única para conectar tu TPV</p>
              <Button onClick={handleGenerate} disabled={generating} size="sm" data-testid="btn-generate-webhook">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Generar Webhook
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
